import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Database,
  HardDrive,
  RefreshCw,
  ShieldAlert,
  XCircle,
  Zap,
} from 'lucide-react'
import { auditApi } from '@/lib/audit-api'
import {
  AUDIT_OPERATIONS,
  AUDIT_SOURCES,
  AuditLogOptimized,
  AuditOperation,
  AuditSource,
  ChangedField,
  CRITICAL_FIELDS,
  OPERATION_COLORS,
  SOURCE_COLORS,
} from '@/types/audit'

interface Props {
  projectId: number
}

function formatRelative(timestamp: string): string {
  const then = new Date(timestamp).getTime()
  const now = Date.now()
  const diffSec = Math.round((now - then) / 1000)
  if (diffSec < 60) return 'just now'
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function isDiff(v: unknown): v is ChangedField {
  return v !== null && typeof v === 'object' && 'old' in (v as object) && 'new' in (v as object)
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function isCriticalRow(log: AuditLogOptimized): boolean {
  if (log.operation === 'DELETE') return true
  if (!log.changed_fields) return false
  for (const k of Object.keys(log.changed_fields)) {
    if (CRITICAL_FIELDS.has(k)) return true
  }
  return false
}

function getOperationIcon(operation: string) {
  switch (operation) {
    case 'INSERT':
    case 'SCRAP_INSERT':
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case 'UPDATE':
    case 'SCRAP_UPDATE':
      return <RefreshCw className="h-4 w-4 text-blue-500" />
    case 'DELETE':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'SCRAP_ERROR':
      return <AlertCircle className="h-4 w-4 text-red-500" />
    case 'SCRAP_WARNING':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />
    case 'SCRAP_SKIPPED':
      return <Clock className="h-4 w-4 text-gray-500" />
    default:
      return <Activity className="h-4 w-4 text-gray-500" />
  }
}

function getSourceIcon(source: string) {
  switch (source) {
    case 'scraper':
      return <Zap className="h-4 w-4 text-blue-500" />
    case 'manual':
      return <Database className="h-4 w-4 text-green-500" />
    case 'system':
      return <HardDrive className="h-4 w-4 text-gray-500" />
    default:
      return <Activity className="h-4 w-4 text-gray-500" />
  }
}

function renderChangedFields(cf: Record<string, unknown> | null) {
  if (!cf) return null
  const entries = Object.entries(cf)
  if (entries.length === 0) {
    return <span className="text-gray-400 text-sm italic">no field changes</span>
  }
  return (
    <div className="space-y-1">
      {entries.map(([field, change]) =>
        isDiff(change) ? (
          <div key={field} className="flex items-center space-x-2 text-sm">
            <span className="font-medium text-gray-700">{field}:</span>
            <span className="text-red-600 line-through">{stringify(change.old)}</span>
            <span className="text-gray-400">→</span>
            <span className="text-green-600 font-medium">{stringify(change.new)}</span>
          </div>
        ) : (
          <div key={field} className="flex items-center space-x-2 text-sm">
            <span className="font-medium text-gray-700">{field}:</span>
            <span className="text-gray-700">{stringify(change)}</span>
          </div>
        ),
      )}
    </div>
  )
}

function renderUserCell(log: AuditLogOptimized) {
  if (!log.user_id) {
    return <span className="text-gray-400 text-sm">—</span>
  }
  const fullName = [log.user_first_name, log.user_last_name].filter(Boolean).join(' ')
  const display = fullName || log.user_email || log.user_id.slice(0, 8)
  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm text-gray-900" title={log.user_email || log.user_id}>
        {display}
      </span>
      {log.user_role && (
        <span
          className={`px-1.5 py-0.5 text-xs font-medium rounded ${
            log.user_role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {log.user_role}
        </span>
      )}
    </div>
  )
}

/**
 * Project-scoped audit history. Filters audit_logs_optimized to rows where
 * table_name = 'project' AND record_id = <this project>. Scraper churn fields
 * are pre-filtered by the DB trigger's allowlist — so what shows up here is
 * meaningful business-field changes only.
 *
 * Unit-level changes are not included in v1 to keep the query simple. Add a
 * server-side UNION endpoint later if "show everything for this project" is
 * needed.
 */
export function ProjectHistory({ projectId }: Props) {
  const [page, setPage] = useState(1)
  const limit = 50

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['project-history', projectId, page, limit],
    queryFn: () =>
      auditApi.getOptimizedAuditLogs({
        table: 'project',
        recordId: String(projectId),
        page,
        limit,
      }),
    keepPreviousData: true,
  })

  if (isLoading) {
    return (
      <div className="card flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading history…</span>
      </div>
    )
  }

  const rows = data?.data ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Project history</h3>
          <p className="text-sm text-gray-500 mt-1">
            Audit trail for this project — field changes, status transitions, and source attribution.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          <Activity className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No history recorded for this project yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            Audit rows are written when a tracked business field changes (sold_out, status,
            developer/broker/architect, website, etc.).
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8" />
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    When
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Operation
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((log) => {
                  const critical = isCriticalRow(log)
                  return (
                    <tr key={log.id} className={critical ? 'bg-amber-50' : ''}>
                      <td className="px-3 py-3 whitespace-nowrap align-top">
                        {critical && (
                          <span title="Critical field changed">
                            <ShieldAlert className="h-4 w-4 text-amber-600" />
                          </span>
                        )}
                      </td>
                      <td
                        className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 align-top"
                        title={new Date(log.timestamp).toLocaleString()}
                      >
                        {formatRelative(log.timestamp)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap align-top">
                        <div className="flex items-center space-x-2">
                          {getOperationIcon(log.operation)}
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              OPERATION_COLORS[log.operation as AuditOperation] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {AUDIT_OPERATIONS[log.operation as AuditOperation] || log.operation}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap align-top">
                        <div className="flex items-center space-x-2">
                          {getSourceIcon(log.source || '')}
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              SOURCE_COLORS[log.source as AuditSource] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {AUDIT_SOURCES[log.source as AuditSource] || log.source || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap align-top">{renderUserCell(log)}</td>
                      <td className="px-3 py-3 align-top">
                        {renderChangedFields(log.changed_fields)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {data?.meta && data.meta.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * limit + 1} to{' '}
                {Math.min(page * limit, data.meta.total)} of {data.meta.total.toLocaleString()}{' '}
                results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-2 py-1 text-sm text-gray-600">
                  Page {page} of {data.meta.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.meta.totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
